import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageSurveyComponent } from '@unity/abstract/abstract-manage-survey.component';
import {
  ClientExpenseDetailedDto,
  ClientExpensesClient,
  ClientExpenseUpdateDto,
  FormDataDto,
  TransitionMiniDto,
  TransitionRequestDto,
} from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { SurveyDataService } from '@unity/services/survey-data.service';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardManageSurveySharedComponent } from '@unity/shared/composed/card-manage-survey-shared/card-manage-survey-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'unity-manage-client-expense-page',
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_BUTTON,
    PageContainerComponent,
    AuthorizeDirective,
    ActionDeleteSharedComponent,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
    CardManageSurveySharedComponent,
  ],
  templateUrl: './manage-client-expense-page.component.html',
  styleUrl: './manage-client-expense-page.component.scss',
})
export class ManageClientExpensePageComponent extends AbstractManageSurveyComponent<ClientExpenseDetailedDto> {
  private readonly clientExpensesClient = inject(ClientExpensesClient);
  private readonly surveyDataService = inject(SurveyDataService);

  /* Inputs from Route */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $clientExpenseId = input.required({ alias: 'clientExpenseId', transform: (value: string) => parseInt(value) });

  /* Impl */
  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      const agreementId = this.$agreementId();
      return agreementId && resourceId ? { agreementId, resourceId } : undefined;
    },
    stream: ({ params: { agreementId, resourceId } }) => this.clientExpensesClient.getOneForAgreementAsDetailed(agreementId, resourceId),
  });

  protected override readonly $formSchema = computed(() => {
    const resource = this.$resource();
    return resource?.form_schema;
  });

  protected override updateResourceFn$ = (resourceId: number, data: FormDataDto, transition?: TransitionMiniDto) => {
    const agreementId = untracked(this.$agreementId);

    const updateDto: ClientExpenseUpdateDto = {
      transition_id: transition?.id ?? null,
      ...data,
    };
    return this.clientExpensesClient.updateOneForAgreement(agreementId, resourceId, updateDto);
  };

  protected override deleteResourceFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.clientExpensesClient.deleteOneForAgreement(agreementId, resourceId);
  };

  protected override editEntitlement: UnityEntitlementPath = 'ClientExpense:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'ClientExpense:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'ClientExpense:Delete';
  protected override resourceName = 'Client Expense';
  protected override $resourceId = this.$clientExpenseId;

  protected override availableTransitionsFn$ = (_resourceId: number) => {
    const agreementId = this.$agreementId();
    return this.availableTransitionsClient.getAllForClientExpensesAsMini(agreementId, _resourceId);
  };

  protected override doTransitionFn$ = (_resourceId: number, transition: TransitionRequestDto) => {
    const agreementId = this.$agreementId();
    return this.transitionsClient.setForClientExpense(agreementId, _resourceId, transition);
  };

  /* Computed */
  protected readonly $returnToAgreementAction = computed(() => {
    const agreementId = this.$agreementId();
    return QuickActions.ReturnToAgreement(agreementId);
  });

  protected readonly $contentHeader = computed(() => {
    const clientExpense = this.mainResource.value();
    return `Client Expense: ${clientExpense?.reference_number_display}`;
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const clientExpense = this.mainResource.value();
    return `Client: ${clientExpense?.client.first_name} ${clientExpense?.client.last_name} (${clientExpense?.client.reference_number_display})`;
  });

  protected readonly $contentHeaderTertiary = computed(() => {
    const clientExpense = this.mainResource.value();
    return `Agreement: ${clientExpense?.agreement.name}`;
  });

  /* Data */
  public constructor() {
    super();

    effect(() => {
      const agreementId = this.$agreementId();
      this.surveyDataService.setAgreementId(agreementId);
    });
  }
}
